import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { AuthShell, ErrorBox, Field, PasswordRules, inputCls, passwordProblems, primaryBtn } from "@/components/auth/AuthUi";

// One-time setup / reset link: /account/setup#token=...
// The token lives in the URL FRAGMENT (never sent to any server by the browser). It is read once
// into memory and the fragment is removed from the address bar immediately.
function readTokenFromHash() {
  const m = /(?:^|[#&])token=([^&]+)/.exec(window.location.hash || "");
  return m ? decodeURIComponent(m[1]) : "";
}

export default function AccountSetup() {
  const [token] = useState(() => readTokenFromHash());
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("checking"); // checking | ready | invalid | done
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
    if (!token) { setState("invalid"); return; }
    api.post("/auth/setup/inspect", { token })
      .then((r) => { setInfo(r.data); setState("ready"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/setup/complete", { token, password });
      setState("done");
    } catch (e2) {
      setErr(apiError(e2));
      if (e2?.response?.data?.code === "INVALID_LINK") setState("invalid");
    } finally { setBusy(false); }
  };

  if (state === "checking") {
    return <AuthShell title="Checking link…" testId="setup-checking"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></AuthShell>;
  }
  if (state === "invalid") {
    return (
      <AuthShell title="Link invalid or expired" testId="setup-invalid"
        subtitle="Setup and reset links work once and expire after 24 hours. Ask an administrator for a new link.">
        <Link to="/login" className="text-sm text-[#5b9bff]">Go to sign in</Link>
      </AuthShell>
    );
  }
  if (state === "done") {
    return (
      <AuthShell title="Password set" testId="setup-done">
        <div className="flex items-center gap-2 text-sm text-slate-300"><CheckCircle2 className="h-5 w-5 text-[#00C805]" /> You can now sign in with your new password.</div>
        <Link to="/login" data-testid="setup-go-login" className={`w-full ${primaryBtn}`}>Sign in</Link>
      </AuthShell>
    );
  }

  const isReset = info?.purpose === "RESET";
  return (
    <AuthShell title={isReset ? "Reset your password" : "Set up your account"} testId="setup-form"
      subtitle={`${info?.name ? info.name + " · " : ""}${info?.email} · link expires ${dateTime(info?.expires_at)}`}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="New password">
          <input data-testid="setup-password" type="password" autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} className={inputCls} autoFocus />
        </Field>
        <Field label="Confirm new password">
          <input data-testid="setup-confirm" type="password" autoComplete="new-password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
        </Field>
        <PasswordRules password={password} confirm={confirm} email={info?.email} />
        <ErrorBox testId="setup-error">{err}</ErrorBox>
        <button type="submit" data-testid="setup-submit" className={`w-full ${primaryBtn}`}
          disabled={busy || passwordProblems(password, confirm, info?.email).length > 0}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isReset ? "Reset password" : "Create password"}
        </button>
      </form>
    </AuthShell>
  );
}
